/* The open call. The most important page on a station that needs programming
 * before it needs a back catalogue.
 *
 * TWO COLUMNS, and the left one is not decoration. A form can carry the fields
 * and nothing else: it cannot say that nobody here is paid, that promos go to a
 * different address, or that we will not reply to everyone. Left is the honest
 * copy; right is the form. Somebody who reads only the left column has still
 * learned whether this is for them, which saves them an hour and saves us a
 * rejection.
 *
 * LINK ONLY — decision D4. There is no file input on this page and there is not
 * going to be one. A link keeps large uploads off the highest-volume,
 * lowest-trust surface the station has, and it makes spam cost the spammer a
 * hosted file. Uploads happen later, behind a signed link, once a booking
 * exists. The form says so in a line of copy rather than leaving somebody
 * hunting for the button.
 *
 * THE MARQUEE IS DELIBERATELY OFF ON THIS PAGE. A form is the one place where a
 * mis-click is expensive, so the status strip below the nav keeps the borders,
 * the ink field and the mono type, and drops the motion and the hard
 * inversions. Nothing on this page moves except the word counter. (The global
 * ticker lives in App.tsx and is not ours to remove; this page adds no motion
 * of its own and no hover inversion on anything inside the form.)
 *
 * ERRORS ARE RENDERED WHERE THEY HAPPENED. api.ts hands back a 422 keyed by
 * field, and every one of those messages was written for a musician who has
 * just spent an hour on a pitch. Each lands under its own input with all three
 * redundant signals from the design: the heavier rule, the magenta block on the
 * left, and the words. Never colour alone, and never one red box at the top
 * that throws the specifics away.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, isAbortError } from '../api';
import { Chip, ErrorNote, Field, FieldError, Spinner, slotHostNames } from '../components';
import { formatTime, tzLabel } from '../format';
import { usePlayer } from '../player';
import type {
  GenreOut,
  LiveState,
  SubmissionFormat,
  SubmissionInput,
  SubmissionKind,
} from '../types';

/* --------------------------------------------------------------- constants */

/** hgr/schemas.py: MAX_PITCH_WORDS. Same number, same counting rule. */
const MAX_PITCH_WORDS = 300;

/** hgr/schemas.py: MAX_GENRE_SLUGS. */
const MAX_GENRES = 3;

/** `count_words` on the server is `len(re.findall(r"\S+", text))`. This is it. */
function countWords(text: string): number {
  return (text.match(/\S+/g) ?? []).length;
}

interface KindOption {
  value: SubmissionKind;
  label: string;
  blurb: string;
  /** Outsiders is a pre-recorded strand by definition. */
  forcesPrerecorded?: boolean;
}

const KINDS: KindOption[] = [
  {
    value: 'oneoff',
    label: 'One-off show',
    blurb: 'Live from the booth or pre-recorded. A single slot, 60–120 min.',
  },
  {
    value: 'residency',
    label: 'Monthly residency',
    blurb: 'Same slot every month for a season. We ask for six.',
  },
  {
    value: 'outsiders',
    label: 'Outsiders',
    blurb:
      'Our pre-recorded strand. Beatless, spoken word, field recordings, anything that isn’t a DJ set.',
    forcesPrerecorded: true,
  },
];

const FORMATS: { value: SubmissionFormat; label: string; blurb: string }[] = [
  { value: 'live', label: 'Live from the booth', blurb: 'You come in and play it.' },
  { value: 'prerecorded', label: 'Pre-recorded', blurb: 'You send the file after we book you.' },
];

/* ------------------------------------------------------------------- state */

interface FormState {
  kind: SubmissionKind;
  format: SubmissionFormat;
  artistName: string;
  email: string;
  linkUrl: string;
  genres: string[];
  pitch: string;
  rights: boolean;
}

const EMPTY: FormState = {
  kind: 'oneoff',
  format: 'prerecorded',
  artistName: '',
  email: '',
  linkUrl: '',
  genres: [],
  pitch: '',
  rights: false,
};

/** Every field key the server can key a 422 to, in the order they appear. */
const FIELD_ORDER = [
  'kind',
  'format',
  'artist_name',
  'email',
  'link_url',
  'genre_slugs',
  'pitch',
  'rights_accepted',
] as const;

type FieldKey = (typeof FIELD_ORDER)[number];

type FieldErrors = Partial<Record<FieldKey, string>>;

/**
 * The checks worth doing before we spend somebody's round trip on them.
 *
 * Deliberately thin. The server is the truth and its messages are better
 * written than anything we would put here, so this only catches the four
 * things that are certain without asking: an empty required field, a pitch
 * over the ceiling, more than three genres, and the unticked warranty.
 */
function localErrors(form: FormState): FieldErrors {
  const errors: FieldErrors = {};

  if (!form.artistName.trim()) {
    errors.artist_name = 'We need a name to put on the schedule.';
  }
  if (!form.email.trim()) {
    errors.email = 'Without an address there is no way to tell you yes.';
  }
  if (!form.linkUrl.trim()) {
    errors.link_url = 'One link. It is the only thing we listen to at this stage.';
  }
  if (!form.pitch.trim()) {
    errors.pitch = 'A few sentences is plenty. An empty box tells us nothing.';
  } else if (countWords(form.pitch) > MAX_PITCH_WORDS) {
    const over = countWords(form.pitch) - MAX_PITCH_WORDS;
    errors.pitch = `That is ${over} word${over === 1 ? '' : 's'} over. Three hundred is the ceiling.`;
  }
  if (form.genres.length > MAX_GENRES) {
    errors.genre_slugs = 'Three genres, maximum. The pitch is the place for the rest.';
  }
  if (!form.rights) {
    errors.rights_accepted = 'We cannot broadcast a set without this one.';
  }

  return errors;
}

/* -------------------------------------------------------------------- page */

export default function Submit() {
  const { live } = usePlayer();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [genres, setGenres] = useState<GenreOut[] | null>(null);
  const [genresError, setGenresError] = useState<unknown>(null);
  const [genresLoading, setGenresLoading] = useState(true);
  const [genresAttempt, setGenresAttempt] = useState(0);

  const [errors, setErrors] = useState<FieldErrors>({});
  /** 422 messages keyed to something this page does not draw a field for. */
  const [strayErrors, setStrayErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [sent, setSent] = useState<{ id: string; message: string } | null>(null);

  const formRef = useRef<HTMLFormElement | null>(null);
  const sentRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const ids = useId();
  const fieldId = useCallback((name: string) => `${ids}-${name}`, [ids]);

  useEffect(() => {
    document.title = 'Send us a set · Higher Ground Radio';
  }, []);

  /* The genre vocabulary. Every genre, including the ones nobody has broadcast
     yet — the archive needs counts to decide what to draw, this form does not. */
  useEffect(() => {
    const controller = new AbortController();
    setGenresLoading(true);
    setGenresError(null);

    api
      .getGenres(controller.signal)
      .then((rows) => {
        setGenres(rows);
        setGenresLoading(false);
      })
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setGenresError(error);
        setGenresLoading(false);
      });

    return () => controller.abort();
  }, [genresAttempt]);

  /* A submission in flight when somebody navigates away is a submission we no
     longer have anywhere to put the answer. */
  useEffect(() => () => abortRef.current?.abort(), []);

  const words = countWords(form.pitch);
  const overBy = Math.max(0, words - MAX_PITCH_WORDS);
  const genreLimitReached = form.genres.length >= MAX_GENRES;

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  }, []);

  /** Clearing an error as it is fixed, so the page stops shouting once it is. */
  const clearError = useCallback((key: FieldKey) => {
    setErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key];
      return next;
    });
  }, []);

  const chooseKind = useCallback(
    (option: KindOption) => {
      setForm((previous) => ({
        ...previous,
        kind: option.value,
        format: option.forcesPrerecorded ? 'prerecorded' : previous.format,
      }));
      clearError('kind');
    },
    [clearError],
  );

  const toggleGenre = useCallback(
    (slug: string) => {
      setForm((previous) => {
        const has = previous.genres.includes(slug);
        if (has) return { ...previous, genres: previous.genres.filter((s) => s !== slug) };
        if (previous.genres.length >= MAX_GENRES) return previous;
        return { ...previous, genres: [...previous.genres, slug] };
      });
      clearError('genre_slugs');
    },
    [clearError],
  );

  /** Put the cursor on the first thing that needs another look. */
  const focusFirstError = useCallback(
    (found: FieldErrors) => {
      const first = FIELD_ORDER.find((key) => found[key]);
      if (!first) return;
      const node = formRef.current?.querySelector<HTMLElement>(`[data-field="${first}"]`);
      node?.focus();
      node?.scrollIntoView({ block: 'center' });
    },
    [formRef],
  );

  const submit = useCallback(async () => {
    const found = localErrors(form);
    setStrayErrors([]);
    setFailure(null);

    if (Object.keys(found).length > 0) {
      setErrors(found);
      focusFirstError(found);
      return;
    }

    setErrors({});
    setSubmitting(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const payload: SubmissionInput = {
      kind: form.kind,
      format: form.format,
      artist_name: form.artistName.trim(),
      // The open call asks for the minimum that lets us answer. A real name and
      // a city are collected later on the signed page, where there is a booking
      // and a reason to hand them over.
      real_name: null,
      email: form.email.trim(),
      city: null,
      link_url: form.linkUrl.trim(),
      pitch: form.pitch.trim(),
      genre_slugs: form.genres,
      rights_accepted: form.rights,
    };

    try {
      const result = await api.postSubmission(payload, controller.signal);
      setSent({ id: result.id, message: result.message });
      setSubmitting(false);
    } catch (error: unknown) {
      if (isAbortError(error)) return;
      setSubmitting(false);

      if (error instanceof ApiError && error.isValidation) {
        const mapped: FieldErrors = {};
        const stray: string[] = [];
        for (const [key, message] of Object.entries(error.fields)) {
          if ((FIELD_ORDER as readonly string[]).includes(key)) {
            mapped[key as FieldKey] = message;
          } else {
            stray.push(message);
          }
        }
        setErrors(mapped);
        setStrayErrors(stray);
        if (Object.keys(mapped).length > 0) focusFirstError(mapped);
        return;
      }

      setFailure(error);
    }
  }, [form, focusFirstError]);

  const reset = useCallback(() => {
    setSent(null);
    setForm(EMPTY);
    setErrors({});
    setStrayErrors([]);
    setFailure(null);
  }, []);

  /* Focus lands on the confirmation, because the form it replaced is gone and
     a keyboard user would otherwise be standing on nothing. */
  useEffect(() => {
    if (sent) sentRef.current?.focus();
  }, [sent]);

  const errorCount = Object.keys(errors).length + strayErrors.length;

  return (
    <div className="sub">
      <style>{CSS}</style>

      {/* The static strip. Same ink field and mono type as the marquee, no
          motion: see the note at the top of this file. */}
      <div className="sub__status">
        <span className="mono-13 sub__statuslive">Submissions open</span>
        <span className="mono-13 sub__statusmid">
          Open all year · two of us read everything that arrives
        </span>
        <span className="mono-13 sub__statusair">{onAirLine(live)}</span>
      </div>

      <section className="sec sub__head">
        <h1>
          Send us
          <br />a set
        </h1>
        <div className="mono sec__meta sub__headnote">
          Two people read every submission. We reply to the ones that fit, usually inside three
          weeks. We can’t reply to everyone — that’s not indifference, it’s arithmetic.
        </div>
      </section>

      <div className="sub__wrap">
        <aside className="sub__side">
          <div className="sub__kv">
            <h2 className="mono sub__kvh">What we’re looking for</h2>
            <ul>
              <li>60 to 120 minutes. Longer if you can hold it.</li>
              <li>
                House in the broad sense — gospel, deep, Chicago, disco edits, dub, electro,
                whatever is next to it.
              </li>
              <li>Ambient and beatless for the daytime slots.</li>
              <li>People who have never had a radio show. Genuinely.</li>
              <li>Amsterdam, Rotterdam, Barcelona, anywhere — pre-recorded travels fine.</li>
            </ul>
          </div>

          <div className="sub__kv">
            <h2 className="mono sub__kvh">What we’re not</h2>
            <ul>
              <li data-no="true">
                A promo channel. New releases go to{' '}
                <a href="mailto:highergroundradioshow@gmail.com">highergroundradioshow@gmail.com</a>, not here — we do not play
                them on the shows either, that is the host’s call.
              </li>
              <li data-no="true">
                Paid. Nobody at the station is paid, including us. There is no fee for a show and
                no fee to us for putting one on.
              </li>
              <li data-no="true">Interested in a bio written in the third person.</li>
            </ul>
          </div>

          <div className="sub__kv">
            <h2 className="mono sub__kvh">How it works</h2>
            <ul>
              <li>You send a link. Not a file — there is nothing to upload at this stage.</li>
              <li>
                If it fits, we email you a private page to fill in artwork, a tracklist and show
                notes. No account, no password: the link is the key.
              </li>
              <li>We book you a slot and send your stream key and a page on how to connect.</li>
              <li>
                After it airs, a set that is directly cleared for public on-demand use can join
                our SoundCloud archive with your name on it. We confirm that permission in writing;
                a set whose rights are not clear stays out of the public archive.
              </li>
            </ul>
          </div>

          <div className="sub__access">
            <div className="mono">Access</div>
            <p>
              The booth is on the ground floor and step-free. The record store we broadcast from
              has a step at the entrance — tell us and we’ll meet you there. Pre-recorded is always
              an option, no explanation needed.
            </p>
            <p>
              What is not accessible: there is no lift to the office above the booth, and we have
              no hearing loop. Email{' '}
              <a href="mailto:highergroundradioshow@gmail.com">
                highergroundradioshow@gmail.com
              </a>{' '}
              if you need the full accessibility statement before visiting.
            </p>
          </div>
        </aside>

        <div className="sub__form">
          {sent ? (
            <div className="sub__done" ref={sentRef} tabIndex={-1}>
              <div className="mono dim">Received</div>
              <h2 className="t-h2 sub__donetitle">It arrived.</h2>
              <p className="t-body">{sent.message}</p>
              <p className="t-body">
                One email is on its way to confirm that, and it is the only automatic one you will
                get. After that a person reads it. If it fits we write back, usually inside three
                weeks; if we cannot use it you may not hear from us at all, which is the part we
                like least about this.
              </p>
              <div className="sub__ref">
                <span className="mono dim">Your reference</span>
                <span className="mono-13">{sent.id}</span>
                <span className="mono dim">
                  Quote it if you need to ask us anything about this submission.
                </span>
              </div>
              <div className="sub__doneacts">
                <Link className="btn" to="/schedule">
                  See what is booked
                </Link>
                <Link className="btn-ghost" to="/archive">
                  Listen to the archive
                </Link>
                <button type="button" className="btn-ghost" onClick={reset}>
                  Send another
                </button>
              </div>
            </div>
          ) : (
            <form
              ref={formRef}
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              {errorCount > 0 ? (
                <div className="sub__summary" role="alert">
                  <div className="sub__summaryblk" aria-hidden="true" />
                  <div className="sub__summarybody">
                    <div className="mono">
                      {errorCount === 1
                        ? 'One field needs another look'
                        : `${errorCount} fields need another look`}
                    </div>
                    <p className="t-body sub__summarytext">
                      Nothing was sent and nothing was lost. Each one is marked below, under the
                      field it belongs to.
                    </p>
                    {strayErrors.length > 0 ? (
                      <ul className="sub__straylist">
                        {strayErrors.map((message) => (
                          <li key={message}>{message}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {failure ? (
                <ErrorNote
                  error={failure}
                  what="your submission"
                  onRetry={() => void submit()}
                  retryLabel="Send it again"
                  className="sub__note"
                >
                  {failure instanceof ApiError && failure.status === 429
                    ? 'That is more submissions than we allow in an hour. Nothing is wrong with this one — wait a little and send it again. The limit is there because the alternative is a queue full of the same link fourteen times.'
                    : undefined}
                </ErrorNote>
              ) : null}

              {/* 01 — what for. Three cards, one radio group. */}
              <fieldset className="fld sub__fieldset" data-error={errors.kind ? 'true' : undefined}>
                <legend className="sub__legend">
                  01 &nbsp;What are you applying for <span className="dim">— required</span>
                </legend>

                <div className="sub__types">
                  {KINDS.map((option, index) => {
                    const selected = form.kind === option.value;
                    return (
                      <label
                        key={option.value}
                        className="sub__type"
                        data-selected={selected ? 'true' : undefined}
                      >
                        <input
                          className="vh"
                          type="radio"
                          name={fieldId('kind')}
                          value={option.value}
                          checked={selected}
                          data-field={index === 0 ? 'kind' : undefined}
                          onChange={() => chooseKind(option)}
                        />
                        <span className="sub__typemark" aria-hidden="true" />
                        <span className="sub__typelabel">{option.label}</span>
                        <span className="sub__typeblurb">{option.blurb}</span>
                        {/* The word, so the accent field is not carrying the
                            state on its own. */}
                        <span className="mono sub__typestate">{selected ? 'Chosen' : 'Pick'}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="sub__fmt">
                  <span className="mono dim">And how would you do it</span>
                  <div className="sub__fmtopts">
                    {FORMATS.map((option, index) => {
                      const forced = currentKind(form.kind).forcesPrerecorded === true;
                      const disabled = forced && option.value === 'live';
                      const selected = form.format === option.value;
                      return (
                        <label
                          key={option.value}
                          className="sub__fmtopt"
                          data-selected={selected ? 'true' : undefined}
                          data-disabled={disabled ? 'true' : undefined}
                        >
                          <input
                            className="vh"
                            type="radio"
                            name={fieldId('format')}
                            value={option.value}
                            checked={selected}
                            disabled={disabled}
                            data-field={index === 0 ? 'format' : undefined}
                            onChange={() => {
                              set('format', option.value);
                              clearError('format');
                            }}
                          />
                          <span className="sub__fmtmark" aria-hidden="true" />
                          <span>
                            <span className="sub__fmtlabel">{option.label}</span>
                            <span className="sub__fmtblurb">{option.blurb}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {currentKind(form.kind).forcesPrerecorded ? (
                    <p className="mono dim sub__hint">
                      Outsiders is a pre-recorded strand, so that one is decided for you.
                    </p>
                  ) : null}
                </div>

                <FieldError id={fieldId('kind-err')} message={errors.kind ?? errors.format} />
              </fieldset>

              {/* 02 — artist name */}
              <Field
                index="02"
                label="Artist name"
                required
                id={fieldId('artist')}
                error={errors.artist_name}
              >
                {(props) => (
                  <input
                    {...props}
                    data-field="artist_name"
                    type="text"
                    autoComplete="nickname"
                    maxLength={160}
                    value={form.artistName}
                    onChange={(event) => {
                      set('artistName', event.target.value);
                      clearError('artist_name');
                    }}
                  />
                )}
              </Field>

              {/* 03 — email */}
              <Field
                index="03"
                label="Email"
                required
                id={fieldId('email')}
                error={errors.email}
                hint="One address. It is the only way we can answer you — there is no account and no inbox on this site."
              >
                {(props) => (
                  <input
                    {...props}
                    data-field="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={form.email}
                    onChange={(event) => {
                      set('email', event.target.value);
                      clearError('email');
                    }}
                  />
                )}
              </Field>

              {/* 04 — the one link. D4 lives here. */}
              <Field
                index="04"
                label="One link to your sound"
                required
                id={fieldId('link')}
                error={errors.link_url}
                hint="Send a link, not a file. SoundCloud, Mixcloud, Bandcamp or YouTube all work. Direct downloads — Dropbox, WeTransfer, a bare .wav — are refused by the form, because nobody here opens a stranger’s file."
              >
                {(props) => (
                  <input
                    {...props}
                    data-field="link_url"
                    type="url"
                    inputMode="url"
                    autoComplete="url"
                    autoCapitalize="off"
                    spellCheck={false}
                    placeholder="https://"
                    maxLength={2048}
                    value={form.linkUrl}
                    onChange={(event) => {
                      set('linkUrl', event.target.value);
                      clearError('link_url');
                    }}
                  />
                )}
              </Field>

              {/* 05 — genres */}
              <fieldset
                className="fld sub__fieldset"
                data-error={errors.genre_slugs ? 'true' : undefined}
                data-field="genre_slugs"
                tabIndex={-1}
              >
                <legend className="sub__legend">
                  05 &nbsp;Genres <span className="dim">— up to three, optional</span>
                </legend>

                {genresLoading ? (
                  <Spinner label="Loading the genre list" />
                ) : genresError ? (
                  <ErrorNote
                    error={genresError}
                    what="the genre list"
                    onRetry={() => setGenresAttempt((n) => n + 1)}
                    className="sub__note"
                  >
                    We could not load the genre list, which is the one part of this form that has
                    to come off the station. Everything else still works — send it without, and say
                    in the pitch what it is.
                  </ErrorNote>
                ) : genres && genres.length > 0 ? (
                  <>
                    <div className="chips" role="group" aria-label="Genres">
                      {genres.map((genre) => {
                        const selected = form.genres.includes(genre.slug);
                        return (
                          <Chip
                            key={genre.slug}
                            color={genre.color_token}
                            selected={selected}
                            disabled={!selected && genreLimitReached}
                            onPress={() => toggleGenre(genre.slug)}
                          >
                            {genre.name}
                          </Chip>
                        );
                      })}
                      <Chip>Not listed → say so in the pitch</Chip>
                    </div>
                    <p className="mono dim sub__hint" aria-live="polite">
                      {form.genres.length} of {MAX_GENRES} chosen
                      {genreLimitReached
                        ? ' · that is the ceiling. Unpick one to swap it.'
                        : ' · a selected chip carries an inset rule as well as its colour.'}
                    </p>
                  </>
                ) : (
                  <p className="mono dim sub__hint">
                    No genres are set up on the station yet. Describe it in the pitch instead —
                    that is where we would look anyway.
                  </p>
                )}

                <FieldError id={fieldId('genres-err')} message={errors.genre_slugs} />
              </fieldset>

              {/* 06 — the pitch, with the live counter */}
              <div className="fld" data-error={errors.pitch ? 'true' : undefined}>
                <label htmlFor={fieldId('pitch')}>
                  <span>
                    06 &nbsp;Tell us what you’d do with two hours{' '}
                    <span className="dim">— required</span>
                  </span>
                  <output
                    className="mono sub__counter"
                    htmlFor={fieldId('pitch')}
                    id={fieldId('pitch-count')}
                    data-over={overBy > 0 ? 'true' : undefined}
                  >
                    {words} / {MAX_PITCH_WORDS} words
                    {overBy > 0 ? ` · ${overBy} over` : ''}
                  </output>
                </label>
                {errors.pitch ? (
                  <div className="fld__errwrap">
                    <div className="fld__errblk" aria-hidden="true" />
                    <textarea
                      id={fieldId('pitch')}
                      data-field="pitch"
                      aria-invalid="true"
                      aria-describedby={`${fieldId('pitch-count')} ${fieldId('pitch-err')}`}
                      value={form.pitch}
                      onChange={(event) => {
                        set('pitch', event.target.value);
                        clearError('pitch');
                      }}
                    />
                  </div>
                ) : (
                  <textarea
                    id={fieldId('pitch')}
                    data-field="pitch"
                    aria-describedby={fieldId('pitch-count')}
                    value={form.pitch}
                    onChange={(event) => {
                      set('pitch', event.target.value);
                      clearError('pitch');
                    }}
                  />
                )}
                <p className="mono dim sub__hint">
                  Words, not characters — a character limit would cut most pitches off
                  mid-sentence. Write it in the first person.
                </p>
                <FieldError id={fieldId('pitch-err')} message={errors.pitch} />
              </div>

              {/* The warranty. */}
              <div className="fld" data-error={errors.rights_accepted ? 'true' : undefined}>
                <label className="sub__check" data-checked={form.rights ? 'true' : undefined}>
                  <input
                    className="vh"
                    type="checkbox"
                    data-field="rights_accepted"
                    checked={form.rights}
                    aria-describedby={errors.rights_accepted ? fieldId('rights-err') : undefined}
                    onChange={(event) => {
                      set('rights', event.target.checked);
                      clearError('rights_accepted');
                    }}
                  />
                  <span className="sub__checkbox" aria-hidden="true">
                    {form.rights ? '✓' : ''}
                  </span>
                  <span className="sub__checktext">
                    I have the rights to broadcast everything in this set, or I’m the one who made
                    it. If a rights claim comes in on this recording, I accept responsibility for
                    it.{' '}
                    <span className="dim">
                      Recorded with a timestamp and your IP address when you submit — we keep it,
                      because it is the evidence a claim asks for.
                    </span>
                  </span>
                </label>
                <FieldError id={fieldId('rights-err')} message={errors.rights_accepted} />
              </div>

              <div className="sub__send">
                <button type="submit" className="btn-lg sub__sendbtn" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send it →'}
                </button>
                {submitting ? (
                  <Spinner label="Sending your submission" />
                ) : (
                  <p className="mono dim sub__sendnote">
                    Nothing to sign up for. No password.
                    <br />
                    You’ll get one email confirming it arrived.
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- fragments */

function currentKind(kind: SubmissionKind): KindOption {
  return KINDS.find((option) => option.value === kind) ?? KINDS[0];
}

/** `Off air · next 19:00 CEST · Ground Floor`, off the player's own feed. */
function onAirLine(live: LiveState | null): string {
  if (!live) return 'Checking what is on air…';

  if (live.live && live.now) {
    const who = slotHostNames(live.now.title, live.now.hosts);
    return `On air · ${live.now.title}${who ? ` w/ ${who}` : ''}`;
  }

  const next = live.next?.[0];
  if (!next) return 'Off air · nothing booked yet';

  const zone = tzLabel('station', next.starts_at).split('·').pop()?.trim() ?? '';
  return `Off air · next ${formatTime(next.starts_at, 'station')}${zone ? ` ${zone}` : ''} · ${
    next.title
  }`;
}

/* `Field` and `FieldError` used to live here. They are the validation idiom of
 * the whole app — a real label, the aria wiring done once, and an error in three
 * redundant signals — and the studio needs the same thing on four tabs, so they
 * are in components/Field.tsx now. The markup is byte-for-byte what it was;
 * `sub__hint` and `sub__errswatch` are still on the elements this page styles.
 */

/* --------------------------------------------------------------------- css
 * Page-scoped, under `.sub`. Everything here is either a class the design
 * canvas draws on this screen and the shared kit does not own, or a layout
 * rule that only this page needs. Borders and shadows come off the two scales;
 * radius is 0; nothing here moves. */

const CSS = `
.sub { display: flex; flex-direction: column; }

.sub__status {
  display: flex; align-items: center; gap: 22px; flex-wrap: wrap;
  min-height: 44px; padding: 8px 28px;
  background: var(--ink); color: var(--on-ink);
  border-bottom: var(--bw) solid var(--edge);
}
.sub .sub__statuslive { color: var(--accent); }
.sub .sub__statusair { margin-left: auto; color: var(--on-ink); }
.sub .sub__statusmid { color: var(--on-ink); }

.sub .sub__head { border-bottom: 0; padding-bottom: 18px; }
.sub .sub__head h1 { font-size: clamp(2.75rem, 7vw, 4.75rem); font-variation-settings: 'wdth' 118, 'wght' 800; }
.sub .sub__headnote {
  max-width: 40ch; font-family: var(--ff-body); font-size: .9375rem;
  letter-spacing: 0; text-transform: none; line-height: 1.5; color: var(--muted);
}

.sub__wrap { display: flex; align-items: stretch; border-bottom: var(--bw) solid var(--edge); }

.sub__side {
  width: 470px; flex-shrink: 0; background: var(--surface);
  border-right: var(--bw) solid var(--edge);
  padding: 34px 28px; display: flex; flex-direction: column; gap: 26px;
}
.sub__kvh { margin: 0 0 10px; color: var(--muted); }
.sub__kv ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.sub__kv li { font-size: .9375rem; line-height: 1.45; padding-left: 18px; position: relative; }
/* A filled square is something we want; a hollow one is something we do not.
   The shape carries it, not the colour. */
.sub__kv li::before {
  content: ""; position: absolute; left: 0; top: 7px;
  width: 8px; height: 8px; background: var(--edge);
}
.sub__kv li[data-no="true"]::before { background: var(--surface); box-shadow: inset 0 0 0 var(--bw-chip) var(--edge); }

.sub__access {
  border: var(--bw) solid var(--edge); background: var(--cyan); color: var(--on-cyan);
  padding: 16px; display: flex; flex-direction: column; gap: 8px;
}
.sub__access p { margin: 0; font-size: .875rem; line-height: 1.45; }
.sub__access a { color: var(--on-cyan); }

.sub__form { flex-grow: 1; min-width: 0; padding: 34px 32px 40px; }
.sub__form form { display: flex; flex-direction: column; gap: 26px; }
.sub .sub__note { margin: 0; }

.sub__fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }
.sub__legend {
  padding: 0; font-family: var(--ff-mono); font-weight: 500; font-size: var(--fs-meta);
  letter-spacing: .08em; text-transform: uppercase;
}
.sub .fld > label { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.sub__counter { margin-left: auto; color: var(--muted); }
.sub__counter[data-over="true"] {
  color: var(--on-magenta); background: var(--magenta);
  padding: 3px 7px; border: var(--bw-chip) solid var(--edge);
}
.sub__hint { margin: 0; line-height: 1.6; letter-spacing: .04em; text-transform: none; }
.sub .fld__errwrap input, .sub .fld__errwrap textarea { flex-grow: 1; min-width: 0; }
.sub__errswatch {
  display: inline-block; width: 14px; height: 14px; flex-shrink: 0;
  background: var(--magenta); border: var(--bw-chip) solid var(--edge);
}
.sub .fld__err { margin: 0; line-height: 1.5; align-items: flex-start; }

/* --- 01: the three type cards --- */
.sub__types { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0; }
.sub__type {
  border: var(--bw) solid var(--edge); margin: 0 -1.5px; padding: 18px;
  display: flex; flex-direction: column; gap: 9px; min-height: 150px;
  background: var(--paper); color: var(--text); cursor: pointer;
}
.sub__type[data-selected="true"] {
  background: var(--accent); color: var(--on-accent);
  box-shadow: inset 0 0 0 var(--bw) var(--edge);
}
.sub__type:has(input:focus-visible) { outline: var(--bw) solid var(--edge); outline-offset: 3px; }
.sub__typemark { width: 22px; height: 22px; border: var(--bw) solid var(--edge); flex-shrink: 0; }
.sub__type[data-selected="true"] .sub__typemark { background: var(--edge); }
.sub__typelabel {
  font-size: 1.1875rem; line-height: 1.05;
  font-variation-settings: 'wdth' 96, 'wght' 800; text-transform: uppercase;
}
.sub__typeblurb { font-size: .8125rem; line-height: 1.45; }
.sub__typestate {
  margin-top: auto; font-family: var(--ff-mono); font-weight: 500; font-size: var(--fs-meta);
  letter-spacing: .08em; text-transform: uppercase;
}
.sub__type:not([data-selected="true"]) .sub__typestate { color: var(--muted); }

/* --- 01b: the format, which the API requires and the card does not carry --- */
.sub__fmt { display: flex; flex-direction: column; gap: 9px; margin-top: 4px; }
.sub__fmtopts { display: flex; gap: 0; flex-wrap: wrap; }
.sub__fmtopt {
  display: flex; align-items: flex-start; gap: 12px;
  border: var(--bw) solid var(--edge); margin: 0 -1.5px; padding: 12px 14px;
  min-height: 56px; background: var(--paper); color: var(--text); cursor: pointer; flex: 1 1 240px;
}
.sub__fmtopt[data-selected="true"] {
  background: var(--accent); color: var(--on-accent);
  box-shadow: inset 0 0 0 var(--bw) var(--edge);
}
.sub__fmtopt[data-disabled="true"] { background: var(--surface); color: var(--muted); cursor: not-allowed; }
.sub__fmtopt:has(input:focus-visible) { outline: var(--bw) solid var(--edge); outline-offset: 3px; }
.sub__fmtmark { width: 18px; height: 18px; border: var(--bw) solid var(--edge); flex-shrink: 0; margin-top: 2px; }
.sub__fmtopt[data-selected="true"] .sub__fmtmark { background: var(--edge); }
.sub__fmtlabel {
  display: block; font-size: .9375rem; line-height: 1.2;
  font-variation-settings: 'wght' 700; text-transform: uppercase; letter-spacing: .04em;
}
.sub__fmtblurb { display: block; font-size: .8125rem; line-height: 1.4; margin-top: 3px; }

/* --- the warranty --- */
/* This one is a <label> and a direct child of .fld, so base.css has already
   set it in 11px uppercase mono. A warranty somebody is agreeing to is body
   copy, so the type is put back here. */
.sub .sub__check {
  display: flex; gap: 14px; align-items: flex-start;
  border: var(--bw) solid var(--edge); background: var(--surface);
  padding: 18px; cursor: pointer;
  font-family: var(--ff-body); font-weight: 400; font-size: var(--fs-body);
  letter-spacing: 0; text-transform: none;
}
.sub .sub__check:has(input:focus-visible) { outline: var(--bw) solid var(--edge); outline-offset: 3px; }
.sub__checkbox {
  width: 26px; height: 26px; border: var(--bw) solid var(--edge); flex-shrink: 0;
  background: var(--paper); color: var(--text);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.0625rem; line-height: 1;
}
.sub .sub__check[data-checked="true"] .sub__checkbox { background: var(--accent); color: var(--on-accent); }
.sub__checktext { font-size: .875rem; line-height: 1.5; }

/* --- the summary above a failed submit --- */
.sub__summary { display: flex; border: var(--bw-on) solid var(--edge); background: var(--paper); }
.sub__summaryblk { width: 14px; flex-shrink: 0; background: var(--magenta); border-right: var(--bw-on) solid var(--edge); }
.sub__summarybody { padding: 16px 20px; display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.sub__summarytext { margin: 0; }
.sub__straylist { margin: 0; padding-left: 20px; font-size: .9375rem; line-height: 1.5; }

/* --- send --- */
.sub__send { display: flex; align-items: center; gap: 26px; margin-top: 6px; flex-wrap: wrap; }
.sub .sub__sendbtn { padding: 24px 44px; font-size: 1.3125rem; }
.sub__sendnote { line-height: 1.7; }

/* --- the confirmation --- */
.sub__done { display: flex; flex-direction: column; gap: 16px; }
.sub__done:focus-visible { outline: var(--bw) solid var(--edge); outline-offset: 8px; }
.sub .sub__donetitle { margin: 0; }
.sub__ref {
  display: flex; flex-direction: column; gap: 6px;
  border: var(--bw) solid var(--edge); background: var(--surface); padding: 14px 16px;
  word-break: break-all;
}
.sub__doneacts { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 6px; }

@media (max-width: 1040px) {
  .sub__wrap { flex-direction: column; }
  .sub__side { width: auto; border-right: 0; border-bottom: var(--bw) solid var(--edge); }
  .sub__types { grid-template-columns: minmax(0, 1fr); }
  .sub__type { margin: -1.5px 0; min-height: 0; }
}
@media (max-width: 760px) {
  .sub__status { padding: 8px 12px; gap: 12px; }
  .sub .sub__statusair { margin-left: 0; }
  .sub__side { padding: 20px 12px; }
  .sub__form { padding: 20px 12px 28px; }
  .sub .sub__sendbtn { padding: 18px 24px; font-size: 1.0625rem; }
}
`;
